import React, { useEffect, useMemo, useState } from 'react'
import { Currency as CurrencyIcon, GreenTick } from '../../../assests/ExportImageIcon'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import { showToast } from '../../../componants/toast/toastService'
import './currency.css'

const POPULAR_CODES = ['INR', 'USD', 'EUR', 'GBP', 'AED']

const BackArrow = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M15 9H4M8 5l-4 4 4 4" stroke="#026D4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const RadioCheck = ({ checked }) => (
    checked
        ? <span className='langRadio langRadio--checked'><img src={GreenTick} alt="" /></span>
        : <span className='langRadio' />
)

const getCurrencyCode = (currency) => currency?.code || currency?.name || ''
const getCurrencyName = (currency) => currency?.name || currency?.title || currency?.code || '—'
const getCurrencySubLabel = (currency) => {
    const code = currency?.code || ''
    const symbol = currency?.symbol || currency?.currency_symbol || ''
    if (code && symbol) return `${code} • ${symbol}`
    return code || symbol || ''
}

const Currency = ({ onBack }) => {
    const [currencies, setCurrencies] = useState([])
    const [selectedCurrency, setSelectedCurrency] = useState('')
    const [initialCurrency, setInitialCurrency] = useState('')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        let mounted = true

        service
            .get(endpoints.users.currencies, {}, { silent: true })
            .then(({ data }) => {
                if (!mounted) return
                const currencyList = Array.isArray(data?.data) ? data.data : []
                setCurrencies(currencyList)
            })
            .catch(() => {
                if (!mounted) return
                setCurrencies([])
            })
            .finally(() => {
                if (mounted) setLoading(false)
            })

        return () => {
            mounted = false
        }
    }, [])
 

    const selectedItem = useMemo(
        () => currencies.find((item) => String(getCurrencyCode(item)) === String(selectedCurrency)),
        [currencies, selectedCurrency]
    )

    const popularCurrencies = useMemo(
        () => currencies.filter((item) => POPULAR_CODES.includes(String(getCurrencyCode(item)).toUpperCase())),
        [currencies]
    )

    const otherCurrencies = useMemo(
        () => currencies.filter((item) => !POPULAR_CODES.includes(String(getCurrencyCode(item)).toUpperCase())),
        [currencies]
    )

    const currentLabel = selectedItem
        ? `${getCurrencyName(selectedItem)}${getCurrencyCode(selectedItem) ? ` (${getCurrencyCode(selectedItem)})` : ''}`
        : selectedCurrency || 'Not set'

    const handleSave = async () => {
        if (!selectedCurrency) {
            showToast('Please select a currency', 'warning')
            return
        }

        if (String(selectedCurrency) === String(initialCurrency)) {
            showToast('No changes to update', 'warning')
            return
        }

        const payload = new FormData()
        payload.append('currency', String(selectedCurrency))

        setSaving(true)
        try {
            const { data } = await service.put(endpoints.users.updateUserProfile, payload, { silent: true })
            const account = data?.data || {}
            const nextCurrency = account.currency || selectedCurrency
            setSelectedCurrency(String(nextCurrency))
            setInitialCurrency(String(nextCurrency))
            showToast(data?.message || 'Currency updated successfully', 'success')
        } catch {
            // handled by service interceptor
        } finally {
            setSaving(false)
        }
    }

    const renderCurrencyList = (list) => {
        if (loading) {
            return (
                <div className='securityMenuItem'>
                    <div className='securityMenuItem__info'>
                        <div className='securityMenuItem__subtitle'>Loading currencies...</div>
                    </div>
                </div>
            )
        }

        if (!list.length) {
            return (
                <div className='securityMenuItem'>
                    <div className='securityMenuItem__info'>
                        <div className='securityMenuItem__subtitle'>No currencies available.</div>
                    </div>
                </div>
            )
        }

        return list.map((currency) => {
            const code = getCurrencyCode(currency)
            return (
                <div
                    className='securityMenuItem'
                    key={currency.id || code}
                    onClick={() => setSelectedCurrency(String(code))}
                >
                    <div className='securityMenuItem__info'>
                        <div className='securityMenuItem__title'>{getCurrencyName(currency)}</div>
                        {getCurrencySubLabel(currency) ? (
                            <div className='securityMenuItem__subtitle'>{getCurrencySubLabel(currency)}</div>
                        ) : null}
                    </div>
                    <RadioCheck checked={String(selectedCurrency) === String(code)} />
                </div>
            )
        })
    }

    return (
        <div className='currencyBox'>
            <div className='tripDocHeader' onClick={onBack}>
                <BackArrow />
                <span className='bsSectionTitle'>Currency</span>
            </div>

            <div className='bsSectionTitle mt-3'>App Preferences</div>
            <div className='bgWhite commonBorder borderRadius20 paddingCommon20 langCurrentBox'>
                <span className='securityMenuItem__icon'>
                    <img src={CurrencyIcon} alt="" />
                </span>
                <div>
                    <div className='langCurrentBox__label'>Current Currency</div>
                    <div className='langCurrentBox__value'>{loading ? 'Loading...' : currentLabel}</div>
                </div>
            </div>

            <div className='langGrid'>
                <div>
                    <div className='bsSectionTitle'>Popular Currencies</div>
                    <div className='bgWhite commonBorder borderRadius20'>
                        {renderCurrencyList(popularCurrencies.length ? popularCurrencies : currencies.slice(0, 5))}
                    </div>
                </div>

                <div>
                    <div className='bsSectionTitle'>All Currencies</div>
                    <div className='bgWhite commonBorder borderRadius20'>
                        {renderCurrencyList(otherCurrencies.length ? otherCurrencies : currencies)}
                    </div>
                </div>
            </div>

            <div className='bsSectionTitle mt-4'>Note</div>
            <div className='bsBox'>
                <p className='bookingConfirmSentText'>
                    Prices will be displayed in the selected currency. Exchange rates may vary at the time of booking.
                </p>
            </div>

            <button
                type='button'
                className='commonBtn w-100 mt-4'
                onClick={handleSave}
                disabled={loading || saving}
            >
                {saving ? 'Saving...' : 'Save Changes'}
            </button>
        </div>
    )
}

export default Currency
